import { LitElement, css, html, nothing } from "lit";
import type { PropertyValues, TemplateResult } from "lit";
import type { Article } from "@newsfeed/shared";
import type { ApiClient } from "../api.js";
import { ApiError } from "../api.js";
import { createArticleHandlers } from "../article-actions.js";
import { sharedStyles } from "../styles.js";
import "../components/article-list.js";

/** Full-text search over all retained articles; results newest-first. */
export class NfSearchView extends LitElement {
  static override properties = {
    api: { attribute: false },
    query: { state: true },
    results: { state: true },
    filter: { state: true },
    loading: { state: true },
    error: { state: true },
    searched: { state: true },
    pending: { state: true },
  };

  api!: ApiClient;
  query = "";
  results: Article[] = [];
  filter: "all" | "unread" = "all";
  loading = false;
  error = "";
  searched = false;
  /** Article id → in-flight state-change action. */
  pending: Record<string, "read" | "keep"> = {};
  private handlers = createArticleHandlers(
    this.api,
    () => this.runSearch(false),
    (id, a) => this.setPending(id, a),
  );

  static override styles = [
    sharedStyles,
    css`
      form {
        display: flex;
        gap: var(--nf-space-8);
        margin-bottom: var(--nf-space-16);
      }
      input {
        flex: 1;
      }
    `,
  ];

  override updated(changed: PropertyValues<this>): void {
    if (changed.has("api"))
      this.handlers = createArticleHandlers(
        this.api,
        () => this.runSearch(false),
        (id, a) => this.setPending(id, a),
      );
  }

  private async runSearch(showLoading = true): Promise<void> {
    if (!this.api || this.query.trim() === "") return;
    if (showLoading) this.loading = true;
    this.error = "";
    this.searched = true;
    try {
      this.results = await this.api.searchArticles({
        q: this.query.trim(),
        limit: 100,
      });
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : String(err);
    } finally {
      if (showLoading) this.loading = false;
    }
  }

  private setPending(id: string, action: "read" | "keep" | null): void {
    const next = { ...this.pending };
    if (action === null) delete next[id];
    else next[id] = action;
    this.pending = next;
  }

  private onSubmit(event: Event): void {
    event.preventDefault();
    void this.runSearch();
  }

  private onFilter(event: Event): void {
    if (event instanceof CustomEvent)
      this.filter = event.detail.filter === "unread" ? "unread" : "all";
  }

  override render(): TemplateResult {
    const shown =
      this.filter === "unread"
        ? this.results.filter((a) => a.readState === "unread")
        : this.results;
    return html`
      <form @submit=${this.onSubmit}>
        <input
          type="search"
          placeholder="Search title, content, author, feed…"
          .value=${this.query}
          @input=${(e: Event) => {
            if (e.target instanceof HTMLInputElement)
              this.query = e.target.value;
          }}
        />
        <button class="primary" type="submit">Search</button>
      </form>
      ${this.error ? html`<div class="card error">${this.error}</div>` : nothing}
      ${
        this.searched
          ? html`<nf-article-list
              .heading=${`Results (${shown.length})`}
              .articles=${shown}
              .filter=${this.filter}
              .loading=${this.loading}
              .pending=${this.pending}
              @filter-change=${this.onFilter}
              @open-article=${this.handlers.open}
              @toggle-read=${this.handlers.read}
              @toggle-keep=${this.handlers.keep}
            ></nf-article-list>`
          : html`<div class="empty">
              Enter a query to search your articles.
            </div>`
      }
    `;
  }
}

customElements.define("nf-search-view", NfSearchView);

declare global {
  interface HTMLElementTagNameMap {
    "nf-search-view": NfSearchView;
  }
}
